import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable} from '../../app/TypeHelpers'
import {AssocOfCondominium} from '../assocDirector/model/AssocOfCondominium'
import AssocManagementRepository from '../assocDirector/AssocManagementRepository'
import PPSelect, {OptionProps} from '../shared/forms/PPSelect'
import AdminGeneralMeetingRepo, {
    GetRoomsWithVotingRightsResponse,
    GetVotingRightsCountByAssocResponse,
    RoomsWithVotingRightsSearchCondition,
    RoomWithVotingRights
} from './AdminGeneralMeetingRepo'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Paper,
    Snackbar,
    SnackbarContent,
    TableCell,
    TableRow
} from '@mui/material'
import {SortDirection} from '@mui/material/TableCell'
import styled from 'styled-components'
import {Colors, DisplaySizeValue, FontWeights} from '../../styledComponents/Constants'
import {
    FullWidthColumnFlexContainer,
    FullWidthRowFlexContainer,
    LeftRowFlexContainer,
    RightRowFlexContainer,
    RowFlexContainer
} from '../../styledComponents/FlexComponents'
import ListSearchFormArea from '../../styledComponents/adminPage/ListSearchFormArea'
import {FlexGridArea} from '../../styledComponents/GridComponents'
import {NatGetRidge, RidgeData} from '../../__NoAutomatedTests/components/ridge/repository/NatGetRidge'
import NatRidgeRepo from '../../__NoAutomatedTests/components/ridge/repository/NatRidgeRepo'
import PPInput from '../shared/forms/PPInput'
import PPCheckboxGroup from '../shared/forms/PPCheckboxGroup'
import {AdminDefaultButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {KEY_UNIT_OWNER_LIST_ASSOC_ID, KEY_UNIT_OWNER_LIST_SEARCH_CONDITION} from '../../app/AdminSessionUtil'

import {convertAssocListToAssocSelectList} from '../assocDirector/shared/AssocComponents'
import SortablePaginationTable from '../../styledComponents/adminPage/SortablePaginationTable'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../styledComponents/adminPage/SortablePaginationTableHeader'
import {SortOrder} from '../shared/SortOrder'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {HKConstants} from '../../HKConstants'
import {COMMON_CONSTANTS} from '../../CommonConstants'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import SortablePaginationTableBody from '../../styledComponents/adminPage/SortablePaginationTableBody'
import {BackLink, StyledLink} from '../../styledComponents/adminPage/Links'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEllipsisH} from '@fortawesome/free-solid-svg-icons'
import {Messages} from '../../Messages'
import {AdminPageHeadingArea} from '../shared/pageBaseComponents/AdminPageHeadingArea'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PPNumericField from '../shared/forms/PPNumericField'
import AuthorityChecker from '../AuthorityChecker'
import {Informations} from '../../__NoAutomatedTests/components/common/repository/NatBaseResponseInterface'
import {Location} from 'react-router-dom'
import {SelectChangeEvent} from '@mui/material/Select'
import {Edit, PersonOff} from '@mui/icons-material'

interface Props {
    condominiumCd: string
    assocManagementRepo: AssocManagementRepository
    ridgeRepo: NatRidgeRepo
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
    eventEmitter: EventEmitterExAdmin
    location: Location<AdminUnitOwnerListPageLocationState>
}

interface State {
    isLoadCompleted: boolean
    assocList: AssocOfCondominium[]
    assocSelectList: OptionProps[]
    votingRightsCountByAssoc: Nullable<number>
    ridgeSelectList: OptionProps[]
    selectedAssocId: number
    searchRidgeCd: string
    searchRoomNumber: string
    searchUnitOwnerConfirmedStatus: string[]
    sortOrders: SortOrder[]
    rowsPerPage: number
    page: number
    totalContentsCount: number
    roomsWithVotingRights: RoomWithVotingRights[]
    isOpenEditMenu: boolean
    editMenuButton: Nullable<HTMLElement>
    isOpenVotingRightsCountEditDialog: boolean
    selectedRoomWithVotingRightsIndex: Nullable<number>
    inputVotingRightsCount: string
    isRegistrationCompleted: boolean
    registrationCompletedMessage: string
}

interface AdminUnitOwnerListPageLocationState {
    informations: Informations[] | null,
}

type SearchConditionForSessionStorage = Omit<RoomsWithVotingRightsSearchCondition, 'sortOrders' | 'page'>

class AdminUnitOwnerListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        const sessionStorageSearchCondition: Nullable<SearchConditionForSessionStorage>
            = SessionStorageHelper.getObject(KEY_UNIT_OWNER_LIST_SEARCH_CONDITION)

        this.state = {
            isLoadCompleted: false,
            assocList: [],
            assocSelectList: [],
            votingRightsCountByAssoc: null,
            ridgeSelectList: [],
            selectedAssocId: 0,
            searchRidgeCd: sessionStorageSearchCondition?.ridgeCd ?? '',
            searchRoomNumber: sessionStorageSearchCondition?.roomNumber ?? '',
            searchUnitOwnerConfirmedStatus: sessionStorageSearchCondition?.unitOwnerConfirmedStatus ?? ['未設定', '済'],
            rowsPerPage: sessionStorageSearchCondition?.rowsPerPage ?? 1000,
            page: COMMON_CONSTANTS.FIRST_PAGE,
            totalContentsCount: 0,
            sortOrders: [
                new SortOrder('ridgeName', 'asc'),
                new SortOrder('roomNumber', 'asc'),
            ],
            roomsWithVotingRights: [],
            isOpenEditMenu: false,
            editMenuButton: null,
            isOpenVotingRightsCountEditDialog: false,
            selectedRoomWithVotingRightsIndex: null,
            inputVotingRightsCount: '',
            isRegistrationCompleted: false,
            registrationCompletedMessage: '',
        }

        this.updateVotingRightsCount = this.updateVotingRightsCount.bind(this)
        this.convertRidgeListToRidgeSelectList = this.convertRidgeListToRidgeSelectList.bind(this)
        this.handleClickVotingRightsCountBulkUpdateButton = this.handleClickVotingRightsCountBulkUpdateButton.bind(this)
        this.handleAssocChange = this.handleAssocChange.bind(this)
        this.handleRidgeChange = this.handleRidgeChange.bind(this)
        this.handleRoomNumberChange = this.handleRoomNumberChange.bind(this)
        this.handleUnitOwnerConfirmedCheckBoxChange = this.handleUnitOwnerConfirmedCheckBoxChange.bind(this)
        this.handleSearchButtonClick = this.handleSearchButtonClick.bind(this)
        this.handlePageChange = this.handlePageChange.bind(this)
        this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this)
        this.handleSortRoomsWithVotingRights = this.handleSortRoomsWithVotingRights.bind(this)
        this.handleClickEditMenuButton = this.handleClickEditMenuButton.bind(this)
        this.handleCloseEditMenu = this.handleCloseEditMenu.bind(this)
        this.handleClickVotingRightsCountEditItem = this.handleClickVotingRightsCountEditItem.bind(this)
        this.handleChangeVotingRightsCount = this.handleChangeVotingRightsCount.bind(this)
        this.handleClickVotingRightsCountEditDialogCancelLink =
            this.handleClickVotingRightsCountEditDialogCancelLink.bind(this)
        this.handleClickVotingRightsCountEditDialogRegisterButton =
            this.handleClickVotingRightsCountEditDialogRegisterButton.bind(this)
        this.renderRegistrationCompletedSnackBar =
            this.renderRegistrationCompletedSnackBar.bind(this)
        this.handleCloseRegistrationCompletedSnackBar =
            this.handleCloseRegistrationCompletedSnackBar.bind(this)
        this.handleClickUnitOwnerUnsetItem = this.handleClickUnitOwnerUnsetItem.bind(this)
        this.handleClickUnitOwnerUnsetDialogCancelLink = this.handleClickUnitOwnerUnsetDialogCancelLink.bind(this)
        this.handleClickUnitOwnerUnsetDialogOKLink = this.handleClickUnitOwnerUnsetDialogOKLink.bind(this)
        this.handleCloseUnitOwnerUnsetCompletedSnackBar = this.handleCloseUnitOwnerUnsetCompletedSnackBar.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {
            condominiumCd,
            assocManagementRepo,
            ridgeRepo,
            eventEmitter,
            asyncCallFunction,
            navigate,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const assocList: Nullable<AssocOfCondominium[]> =
                await asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )

            if (assocList === null) {
                return
            }

            if (assocList.length === 0) {
                navigate('/admin/error')
                return
            }

            const assocSelectList = convertAssocListToAssocSelectList(assocList)
            let selectedAssocId = Number(assocSelectList[0].value)
            const sessionStorageAssocId: Nullable<number>
                = SessionStorageHelper.getNumberItemOrNull(KEY_UNIT_OWNER_LIST_ASSOC_ID)

            if (sessionStorageAssocId !== null) {
                selectedAssocId = Number(assocSelectList.find(
                    assocSelect => assocSelect.value === String(sessionStorageAssocId)
                )?.value ?? assocSelectList[0].value)
            }

            const ridgeList: Nullable<NatGetRidge> =
                await asyncCallFunction(
                    ridgeRepo.getRidgeList.bind(ridgeRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )

            if (ridgeList === null) {
                return
            }

            if (ridgeList.details === null || ridgeList.details?.length === 0) {
                navigate('/admin/error')
                return
            }

            const ridgeSelectList = this.convertRidgeListToRidgeSelectList(ridgeList.details)

            this.setState({
                assocList: assocList,
                assocSelectList: assocSelectList,
                ridgeSelectList: ridgeSelectList,
                selectedAssocId: selectedAssocId,
            }, async () => {
                await Promise.all([
                    this.updateVotingRightsCount(),
                    this.updateRoomsWithVotingRights(),
                ])

                if (this.props.location.state &&
                    this.props.location.state.informations &&
                    this.props.location.state.informations.length > 0) {
                    this.setState({
                        isRegistrationCompleted: true,
                        registrationCompletedMessage: this.props.location.state.informations[0].message,
                    })
                }

                this.setState({
                    isLoadCompleted: true,
                })
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async updateVotingRightsCount(): Promise<void> {
        const {
            condominiumCd,
            adminGeneralMeetingRepo,
            eventEmitter,
            asyncCallFunction,
        } = this.props

        const {
            selectedAssocId,
        } = this.state

        await eventEmitter.showLoading()
        try {
            const result: Nullable<GetVotingRightsCountByAssocResponse> =
                await asyncCallFunction(
                    adminGeneralMeetingRepo.getVotingRightsCountByAssoc.bind(
                        adminGeneralMeetingRepo,
                        condominiumCd,
                        selectedAssocId
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )

            if (result === null) {
                return
            }

            this.setState({
                votingRightsCountByAssoc: result.details,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    convertRidgeListToRidgeSelectList(
        ridgeList: RidgeData[]
    ): OptionProps[] {
        let result: OptionProps[] = []
        result = ridgeList.map((ridge) => {
            return ({
                label: `${ridge.ridgeCd} : ${ridge.ridgeName}`,
                value: `${ridge.ridgeCd}`,
            })
        })
        result.unshift(
            {
                label: '棟名を選択する',
                value: '',
            }
        )
        return result
    }

    async updateRoomsWithVotingRights(): Promise<void> {
        const {
            condominiumCd,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        const {
            selectedAssocId,
            searchRidgeCd,
            searchRoomNumber,
            searchUnitOwnerConfirmedStatus,
            sortOrders,
            rowsPerPage,
            page,
        } = this.state

        const sortOrderStrings: string[] = []
        if (sortOrders) {
            sortOrders.forEach((sortInfo) => {
                sortOrderStrings.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }

        const searchCondition: RoomsWithVotingRightsSearchCondition = {
            ridgeCd: searchRidgeCd !== '' ? searchRidgeCd : null,
            roomNumber: searchRoomNumber !== '' ? searchRoomNumber : null,
            unitOwnerConfirmedStatus: searchUnitOwnerConfirmedStatus,
            sortOrders: sortOrderStrings,
            rowsPerPage: rowsPerPage,
            page: page,
        }
        const {
            sortOrders: _sortOrders,
            page: _page,
            ...searchConditionForSessionStorage
        } = searchCondition
        SessionStorageHelper.setItem(KEY_UNIT_OWNER_LIST_ASSOC_ID, selectedAssocId)
        SessionStorageHelper.setObject(KEY_UNIT_OWNER_LIST_SEARCH_CONDITION, searchConditionForSessionStorage)

        let roomsWithVotingRightsResponse: Nullable<GetRoomsWithVotingRightsResponse>

        await eventEmitter.showLoading()
        try {
            roomsWithVotingRightsResponse =
                await asyncCallFunction(
                    adminGeneralMeetingRepo.getRoomsWithVotingRights.bind(
                        adminGeneralMeetingRepo,
                        condominiumCd,
                        selectedAssocId,
                        searchCondition,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
        } finally {
            eventEmitter.closeLoading()
        }

        if (roomsWithVotingRightsResponse === null) {
            return
        }

        this.setState({
            totalContentsCount: roomsWithVotingRightsResponse.details.totalContentsCount,
            roomsWithVotingRights: roomsWithVotingRightsResponse.details.contents,
            selectedRoomWithVotingRightsIndex: null,
        })
    }

    handleClickVotingRightsCountBulkUpdateButton(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/voting-rights-count/bulk-update`)
    }

    async handleAssocChange(event: SelectChangeEvent<unknown>): Promise<void> {
        const assocId: number = Number(event.target.value)

        this.setState({
                selectedAssocId: assocId,
            },
            async () => {
                await Promise.all([
                    this.updateVotingRightsCount(),
                    this.updateRoomsWithVotingRights(),
                ])
            }
        )
    }

    handleRidgeChange(event: SelectChangeEvent<unknown>): void {
        const ridgeCd: string = String(event.target.value)

        this.setState({
            searchRidgeCd: ridgeCd,
        })
    }

    handleRoomNumberChange(event: React.ChangeEvent<{ value: unknown }>): void {
        const roomNumber: string = String(event.target.value)

        this.setState({
            searchRoomNumber: roomNumber,
        })
    }

    handleUnitOwnerConfirmedCheckBoxChange(label: string, checked: boolean): void {
        const {
            searchUnitOwnerConfirmedStatus,
        } = this.state

        const unitOwnerConfirmedList = new Set(searchUnitOwnerConfirmedStatus)
        if (checked) {
            unitOwnerConfirmedList.add(label)
        } else {
            unitOwnerConfirmedList.delete(label)
        }
        this.setState({
            searchUnitOwnerConfirmedStatus: [...unitOwnerConfirmedList]
        })
    }

    async handleSearchButtonClick(): Promise<void> {
        await this.updateRoomsWithVotingRights()
    }

    handlePageChange(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): void {
        this.setState({
            page: newPage,
        }, this.updateRoomsWithVotingRights)
    }

    handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            rowsPerPage: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, this.updateRoomsWithVotingRights)
    }

    async handleSortRoomsWithVotingRights(sortKey: string): Promise<void> {
        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        let sortOrders: SortOrder[] = []
        if (sortKey === 'roomNumber') {
            sortOrders = [
                new SortOrder('ridgeName', sortDirection),
                new SortOrder(sortKey, sortDirection),
            ]
        } else {
            sortOrders = [new SortOrder(sortKey, sortDirection)]
        }
        this.setState(
            {sortOrders: sortOrders},
            this.updateRoomsWithVotingRights,
        )
    }

    handleClickEditMenuButton(event: React.MouseEvent<HTMLElement>, index: number): void {
        this.setState({
            selectedRoomWithVotingRightsIndex: index,
            isOpenEditMenu: true,
            editMenuButton: event.currentTarget,
            inputVotingRightsCount: String(this.state.roomsWithVotingRights[index].votingRightsCount),
        })
    }

    closeEditMenu(): void {
        this.setState({
            isOpenEditMenu: false,
            editMenuButton: null,
        })
    }

    handleCloseEditMenu(): void {
        this.closeEditMenu()
    }

    handleClickVotingRightsCountEditItem(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
        this.closeEditMenu()
        this.setState({
            isOpenVotingRightsCountEditDialog: true,
        })
    }

    handleChangeVotingRightsCount(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({inputVotingRightsCount: event.target.value})
    }

    handleClickVotingRightsCountEditDialogCancelLink(): void {
        this.setState({
            isOpenVotingRightsCountEditDialog: false,
        })
    }

    async handleClickVotingRightsCountEditDialogRegisterButton(): Promise<void> {
        const {
            condominiumCd,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        const {
            selectedAssocId,
            inputVotingRightsCount,
            roomsWithVotingRights,
            selectedRoomWithVotingRightsIndex,
        } = this.state

        if (selectedRoomWithVotingRightsIndex === null) {
            return
        }

        const selectedRoomInfo = roomsWithVotingRights[selectedRoomWithVotingRightsIndex]

        await eventEmitter.showLoading()
        try {
            const updateResult = await asyncCallFunction(
                adminGeneralMeetingRepo.updateVotingRightsCount.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    selectedRoomInfo.ridgeCd,
                    selectedRoomInfo.roomNumber,
                    {
                        assocId: selectedAssocId,
                        votingRightsCount: Number(inputVotingRightsCount),
                        version: selectedRoomInfo.votingRightsVersion,
                    }
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )

            if (!updateResult) {
                return
            }

            if (updateResult.informations !== null && updateResult.informations.length > 0) {
                this.setState({
                    isRegistrationCompleted: true,
                    registrationCompletedMessage: updateResult.informations[0].message,
                })
            }

            await Promise.all([
                this.updateVotingRightsCount(),
                this.updateRoomsWithVotingRights(),
            ])
        } finally {
            eventEmitter.closeLoading()
            this.setState({
                isOpenVotingRightsCountEditDialog: false,
            })
        }
    }

    handleCloseRegistrationCompletedSnackBar(): void {
        this.setState({
            isRegistrationCompleted: false,
        })
    }

    async handleClickUnitOwnerUnsetItem(): Promise<void> {
        const {
            roomsWithVotingRights,
            selectedRoomWithVotingRightsIndex,
        } = this.state

        this.closeEditMenu()

        if (selectedRoomWithVotingRightsIndex === null) {
            return
        }

        const selectedRoomInfo = roomsWithVotingRights[selectedRoomWithVotingRightsIndex]

        await this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: Messages.IGMTA0001.errorLevel,
            message: Messages.IGMTA0001.toString(
                this.createRoomNumberString(
                    selectedRoomInfo.ridgeName,
                    selectedRoomInfo.roomNumber,
                ),
            ),
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: this.handleClickUnitOwnerUnsetDialogCancelLink,
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: this.handleClickUnitOwnerUnsetDialogOKLink,
                },
            ],
        })
    }

    handleClickUnitOwnerUnsetDialogCancelLink(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleClickUnitOwnerUnsetDialogOKLink(): Promise<void> {
        const {
            condominiumCd,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        const {
            roomsWithVotingRights,
            selectedRoomWithVotingRightsIndex,
        } = this.state

        if (selectedRoomWithVotingRightsIndex === null) {
            return
        }

        const selectedRoomInfo = roomsWithVotingRights[selectedRoomWithVotingRightsIndex]

        await eventEmitter.showLoading()
        try {
            const updateResult = await asyncCallFunction(
                // @ts-ignore
                adminGeneralMeetingRepo.updateUnitOwnership.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    selectedRoomInfo.ridgeCd,
                    selectedRoomInfo.roomNumber,
                    selectedRoomInfo.unitOwnerSeq,
                    {
                        unitOwnership: null,
                        version: selectedRoomInfo.unitOwnerVersion,
                    }
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )

            if (!updateResult) {
                return
            }

            if (updateResult.informations !== null && updateResult.informations.length > 0) {
                this.setState({
                    isRegistrationCompleted: true,
                    registrationCompletedMessage: updateResult.informations[0].message,
                })
            }

            await Promise.all([
                this.updateVotingRightsCount(),
                this.updateRoomsWithVotingRights(),
            ])
        } finally {
            eventEmitter.closeDialog()
            eventEmitter.closeLoading()
        }
    }

    handleCloseUnitOwnerUnsetCompletedSnackBar(): void {
        this.setState({
            isRegistrationCompleted: false,
        })
    }

    createRoomNumberString(ridgeName: string, roomNumber: string): string {
        if (ridgeName !== '') {
            return ridgeName + ' ' + roomNumber
        } else {
            return roomNumber
        }
    }

    private renderVotingRightsCountBulkUpdateButton(): ReactNode {
        return <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_UNIT_OWNER_MANAGEMENT}>
            <RightRowFlexContainer>
                <AdminDefaultButton
                    onClick={this.handleClickVotingRightsCountBulkUpdateButton}
                >
                    議決権数をまとめて編集する
                </AdminDefaultButton>
            </RightRowFlexContainer>
        </AuthorityChecker>
    }

    renderVotingRightsCountOfAssoc(): ReactNode {
        const {
            eventEmitter,
        } = this.props

        const {
            assocSelectList,
            selectedAssocId,
            votingRightsCountByAssoc,
        } = this.state

        return <Paper>
            <FullWidthColumnFlexContainer>
                <AreaTitle>管理組合の情報</AreaTitle>
                <RowContainer>
                    <ItemContainer flexGrow={1}>
                        <Item>管理組合</Item>
                        <Item flexGrow={1}>
                            <AssocList
                                value={selectedAssocId}
                                data={assocSelectList}
                                onChange={this.handleAssocChange}
                                eventKeys={['categoryId']}
                                eventEmitter={eventEmitter}
                            />
                        </Item>
                    </ItemContainer>
                    <ItemContainer>
                        <Item>議決権総数</Item>
                        <Item>
                            <VotingRightsCount>
                                {votingRightsCountByAssoc && `${formatNumberWithCommas(votingRightsCountByAssoc)}個`}
                            </VotingRightsCount>
                        </Item>
                    </ItemContainer>
                </RowContainer>
            </FullWidthColumnFlexContainer>
        </Paper>
    }

    renderSearchCondition(): ReactNode {
        const {
            searchUnitOwnerConfirmedStatus,
            totalContentsCount,
        } = this.state
        const unitOwnerConfirmedCheckBoxData = [
            {
                label: '未設定',
                checked: searchUnitOwnerConfirmedStatus.includes('未設定'),
            },
            {
                label: '済',
                checked: searchUnitOwnerConfirmedStatus.includes('済'),
            }
        ]
        return <ListSearchFormArea
            onEnterKey={this.handleSearchButtonClick}
            searchResult={totalContentsCount === 0 ?
                {noResultMessage: Messages.IOPCC0014.toString()} : undefined}
        >
            <SearchForm>
                <NonGridDiv className={'searchConditionRow'}>
                    <FlexGridArea area={'ridgeNameSelectLabel'}>
                        棟名
                    </FlexGridArea>
                    <FlexGridArea area={'ridgeNameSelect'}>
                        <RidgeList
                            selectedFlag={this.state.searchRidgeCd === ''}
                            data={this.state.ridgeSelectList}
                            onChange={this.handleRidgeChange}
                            value={this.state.searchRidgeCd}
                        />
                    </FlexGridArea>
                </NonGridDiv>
                <NonGridDiv className={'searchConditionRow'}>
                    <FlexGridArea area={'roomNumberInputLabel'}>
                        部屋番号
                    </FlexGridArea>
                    <FlexGridArea area={'roomNumberInput'}>
                        <RoomNumberInput
                            inputProps={{
                                minLength: 0,
                                maxLength: 4,
                            }}
                            placeholder={'部屋番号を入力する'}
                            onChange={this.handleRoomNumberChange}
                            value={this.state.searchRoomNumber}
                        />
                    </FlexGridArea>
                </NonGridDiv>
                <NonGridDiv className={'searchConditionRow'}>
                    <FlexGridArea area={'unitOwnerConfirmedCheckLabel'}>
                        区分所有者確認
                    </FlexGridArea>
                    <FlexGridArea area={'unitOwnerConfirmedCheck'}>
                        <PPCheckboxGroup
                            row
                            data={unitOwnerConfirmedCheckBoxData}
                            onChangeChecked={this.handleUnitOwnerConfirmedCheckBoxChange}
                        />
                    </FlexGridArea>
                    <FlexGridArea area={'searchButton'}>
                        <AdminDefaultButton onClick={this.handleSearchButtonClick}>
                            検索する
                        </AdminDefaultButton>
                    </FlexGridArea>
                </NonGridDiv>
            </SearchForm>
        </ListSearchFormArea>
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.sortOrders.find(order => order.sortKey === sortKey);
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    renderRoomsWithVotingRights(): ReactNode {
        if (this.state.totalContentsCount === 0) {
            return
        }

        const headers: SortableTableHeaderCell[] = [
            {
                id: 'roomNumber',
                label: '部屋番号',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('roomNumber'),
            },
            {
                id: 'votingRightsCount',
                label: '議決権数',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('votingRightsCount'),
            },
            {
                id: 'unitOwner',
                label: '区分所有者',
                align: 'left',
            },
            {
                id: 'unitOwnerConfirmedStatus',
                label: '区分所有者確認',
                align: 'left',
            },
        ]
        if (judgeAuthorityCheck(HKConstants.CMN.AUTH.EDIT_UNIT_OWNER_MANAGEMENT)) {
            headers.push(
                {
                    id: 'editMenu',
                    label: '操作',
                    align: 'left',
                },
            )
        }
        const rowsPerPageOptions =
            [...new Set([...COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS, 1000])].sort((a, b) => a - b)
        return <PageMainArea>
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.totalContentsCount,
                    rowsPerPage: this.state.rowsPerPage,
                    page: this.state.page,
                    rowsPerPageOptions: rowsPerPageOptions,
                    onPageChange: this.handlePageChange,
                    onRowsPerPageChange: this.handleRowsPerPageChange,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortRoomsWithVotingRights}
                />
                <SortablePaginationTableBody>
                    {this.state.roomsWithVotingRights.map((roomWithVotingRights, index) =>
                        <TableRow key={index}>
                            <TableCell align={'left'}>
                                {this.createRoomNumberString(
                                    roomWithVotingRights.ridgeName,
                                    roomWithVotingRights.roomNumber
                                )}
                            </TableCell>
                            <TableCell align={'left'}>
                                {formatNumberWithCommas(roomWithVotingRights.votingRightsCount)}
                            </TableCell>
                            <TableCell align={'left'}>
                                {roomWithVotingRights.unitOwnerName}
                            </TableCell>
                            <TableCell align={'left'}>
                                {roomWithVotingRights.unitOwnerHasekoId ? '済' : '未設定'}
                            </TableCell>
                            <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_UNIT_OWNER_MANAGEMENT}>
                                <TableCell>
                                    <StyledLink
                                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                                            this.handleClickEditMenuButton(event, index)
                                        }}>
                                        <FontAwesomeIcon icon={faEllipsisH} color={Colors.AccentDark}/>
                                    </StyledLink>
                                </TableCell>
                            </AuthorityChecker>
                        </TableRow>
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        </PageMainArea>
    }

    renderEditMenu(): ReactNode {
        const {
            roomsWithVotingRights,
            selectedRoomWithVotingRightsIndex,
        } = this.state;

        if (selectedRoomWithVotingRightsIndex === null) {
            return
        }

        const selectedRoomInfo = roomsWithVotingRights[selectedRoomWithVotingRightsIndex]

        return <Menu
            open={this.state.isOpenEditMenu}
            onClose={this.handleCloseEditMenu}
            anchorEl={this.state.editMenuButton}
        >
            <MenuItem onClick={this.handleClickVotingRightsCountEditItem}>
                <LeftRowFlexContainer>
                    <Edit/>
                    <Box width={'15px'}/>
                    議決権数を編集する
                </LeftRowFlexContainer>
            </MenuItem>
            {selectedRoomInfo.unitOwnerSeq &&
                <MenuItem onClick={this.handleClickUnitOwnerUnsetItem}>
                    <LeftRowFlexContainer>
                        <PersonOff/>
                        <Box width={'15px'}/>
                        区分所有者を未設定にする
                    </LeftRowFlexContainer>
                </MenuItem>
            }
        </Menu>
    }

    renderVotingRightsCountEditDialog(): ReactNode {
        const {
            assocList,
            selectedAssocId,
            roomsWithVotingRights,
            selectedRoomWithVotingRightsIndex,
            isOpenVotingRightsCountEditDialog,
            inputVotingRightsCount,
        } = this.state

        if (selectedRoomWithVotingRightsIndex === null) {
            return
        }

        const selectedAssocName = assocList.find(
            assocOfCondominium => assocOfCondominium.assocId === selectedAssocId
        )?.assocName

        const selectedRoomInfo = roomsWithVotingRights[selectedRoomWithVotingRightsIndex]

        return <Dialog
            id={'pp-dialog'}
            open={isOpenVotingRightsCountEditDialog}
            transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
            maxWidth={'lg'}
        >
            <DialogArea>
                <StyledDialogTitle>
                    <AdminPageHeadingArea
                        title={'議決権数の編集'}
                    />
                </StyledDialogTitle>
                <StyledDialogContent>
                    <RoomInfoArea>
                        <div>
                            {selectedAssocName}
                        </div>
                        <div>
                            {this.createRoomNumberString(
                                selectedRoomInfo.ridgeName,
                                selectedRoomInfo.roomNumber
                            )}
                        </div>
                    </RoomInfoArea>
                    <VotingRightsCountInputArea>
                        <VotingRightsCountLabel>議決権数</VotingRightsCountLabel>
                        <LeftRowFlexContainer>
                            <StyledPPNumericField
                                InputProps={{
                                    inputProps: {
                                        min: 0,
                                        max: HKConstants.VMN.GMT.MAX_NUMBER_OF_VOTING_RIGHTS_COUNT_PER_ROOM,
                                    },
                                }}
                                value={inputVotingRightsCount}
                                onChange={this.handleChangeVotingRightsCount}
                            />
                            <VotingRightsCountUnit>
                                個
                            </VotingRightsCountUnit>
                        </LeftRowFlexContainer>
                    </VotingRightsCountInputArea>
                </StyledDialogContent>
                <StyledDialogActions>
                    <BackLink onClick={this.handleClickVotingRightsCountEditDialogCancelLink}>
                        キャンセル
                    </BackLink>
                    <AdminPrimaryButton
                        onClick={this.handleClickVotingRightsCountEditDialogRegisterButton}
                        disabled={inputVotingRightsCount === ''}
                    >
                        登録する
                    </AdminPrimaryButton>
                </StyledDialogActions>
            </DialogArea>
        </Dialog>
    }

    renderRegistrationCompletedSnackBar(): ReactNode {
        return <StyledSnackbar
            open={this.state.isRegistrationCompleted}
            anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            autoHideDuration={4000}
            onClose={this.handleCloseRegistrationCompletedSnackBar}
        >
            <SnackbarContent
                style={{backgroundColor: '#00838a', color: 'white', fontWeight: FontWeights.Bold}}
                message={
                    <>
                        <LeftRowFlexContainer>
                            <CheckCircleOutlineIcon titleAccess={'information'}/>
                            <Box width={'5px'}/>
                            {this.state.registrationCompletedMessage}
                        </LeftRowFlexContainer>
                    </>
                }
            ></SnackbarContent>
        </StyledSnackbar>
    }

    render(): ReactNode {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <Box height={'3px'}/>
            {this.renderVotingRightsCountBulkUpdateButton()}
            <Box height={'9px'}/>
            {this.renderVotingRightsCountOfAssoc()}
            <Box height={'24px'}/>
            {this.renderSearchCondition()}
            <Box height={'21px'}/>
            {this.renderRoomsWithVotingRights()}
            {this.renderEditMenu()}
            {this.renderVotingRightsCountEditDialog()}
            {this.renderRegistrationCompletedSnackBar()}
        </>
    }
}

export default createAdminPage(AdminUnitOwnerListPage)

const AreaTitle = styled.div`
  width: 100%;
  padding: 7px 12px;
  font-size: 20px;
  background-color: ${Colors.AccentLight};
`

const RowContainer = styled(FullWidthRowFlexContainer)`
  padding: 0;
  position: relative;
`

const ItemContainer = styled(RowFlexContainer)<{ flexGrow?: number }>`
  justify-content: flex-start;
  ${props => props.flexGrow
      ? `flex-grow: ${props.flexGrow};`
      : ``
  };

  :not(:first-child)::before {
    content: '';
    width: 2px;
    top: 0;
    bottom: 0;
    position: absolute;
    background-color: ${Colors.AccentLight};
  }
`

const Item = styled.div<{ flexGrow?: number }>`
  padding: 19px 24px;
  white-space: nowrap;
  ${props => props.flexGrow
      ? `flex-grow: ${props.flexGrow};`
      : ``
  };
`

const AssocList = styled(PPSelect)`
  width: 100%;
`

const VotingRightsCount = styled.div`
  width: 77px;
  text-align: right;
`

const SearchForm = styled.div`
  display: grid;
  grid-template:
            "ridgeNameSelectLabel ridgeNameSelect ridgeNameSelect"
            "roomNumberInputLabel roomNumberInput roomNumberInput"
            "unitOwnerConfirmedCheckLabel unitOwnerConfirmedCheck searchButton"
              / 186px 1fr max-content;
  padding: 13px 12px 10px;
  row-gap: 26px;
  column-gap: 16px;
`

const NonGridDiv = styled.div`
  display: contents;
`

const RidgeList = styled(PPSelect)<{ selectedFlag: boolean }>`
  color: ${props => props.selectedFlag ? 'rgba(0, 0, 0, 0.38)' : 'inherit'};
  min-width: 300px;
`

const RoomNumberInput = styled(PPInput)`
  width: 500px;
`

const DialogArea = styled.div`
  width: 720px;
  padding: 0;
`

const StyledDialogTitle = styled(DialogTitle)`
  padding: 24px 24px 0 24px;
`

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  padding-top: 6px;
  gap: 16px;
`

const RoomInfoArea = styled.div`
  background-color: ${Colors.Background};
  padding: 16px;
  font-size: 16px;
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const VotingRightsCountInputArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const VotingRightsCountLabel = styled.div`
  font-weight: ${FontWeights.Bold};
`

const StyledPPNumericField = styled(PPNumericField)`
  width: 200px;
`

const VotingRightsCountUnit = styled.div`
  margin-left: 10px;
  font-weight: ${FontWeights.Bold};
`

const StyledDialogActions = styled(DialogActions)`
  padding: 8px 24px 24px 24px;
  justify-content: space-between;
`

const StyledSnackbar = styled(Snackbar)`
  & .MuiPaper-root {
    width: ${DisplaySizeValue.AdminWidthMin - 30}px;
  }
`
